// src/pages/dashboard/nanny/ProfileCompletion.jsx
import { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import {
  CheckCircle2, Upload, Camera, Shield, BookOpen,
  Briefcase, ChevronRight, Plus, Trash2
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

const steps = [
  { id: 1, name: 'Photo', icon: Camera },
  { id: 2, name: 'Guarantor', icon: Shield },
  { id: 3, name: 'Qualifications', icon: BookOpen },
  { id: 4, name: 'Experience', icon: Briefcase },
];

export default function ProfileCompletion() {
  const { user, api, updateUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [profile, setProfile] = useState(user?.profile || {});
  const [uploading, setUploading] = useState(false);
  const [loadingQual, setLoadingQual] = useState(false);     // ← NEW
  const [loadingExp, setLoadingExp] = useState(false);       // ← NEW
  const contentRef = useRef(null);

  // Guarantor form
  const [guarantor, setGuarantor] = useState({
    full_name: '', phone: '', relationship: '', address: '', id_card: null
  });

  // Qualifications
  const [qualifications, setQualifications] = useState(profile.qualifications || []);
  const [newQual, setNewQual] = useState({ qualification_name: '', institution: '', year_obtained: '' });

  // Experience
  const [experiences, setExperiences] = useState(profile.work_experiences || []);
  const [newExp, setNewExp] = useState({
    institution_name: '', position: '', start_date: '', end_date: '', responsibilities: ''
  });

  // Sync from auth
  useEffect(() => {
    if (user?.profile) {
      setProfile(user.profile);
      setQualifications(user.profile.qualifications || []);
      setExperiences(user.profile.work_experiences || []);
    }
  }, [user]);

  // Auto-jump to first incomplete step
  useEffect(() => {
    const next = steps.find(s => {
      if (s.id === 1) return !profile.profile_picture;
      if (s.id === 2) return !profile.guarantors?.length;
      if (s.id === 3) return !profile.qualifications?.length;
      if (s.id === 4) return !profile.work_experiences?.length;
      return false;
    });
    if (next) setCurrentStep(next.id);
  }, [profile]);

  // Animation
  useEffect(() => {
    gsap.fromTo(contentRef.current, { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.5 });
  }, [currentStep]);

  // Progress
  const completed = steps.filter(s => {
    if (s.id === 1) return !!profile.profile_picture;
    if (s.id === 2) return profile.guarantors?.length > 0;
    if (s.id === 3) return qualifications.length > 0;
    if (s.id === 4) return experiences.length > 0;
    return false;
  }).length;
  const percentage = Math.round((completed / steps.length) * 100);
const isProfileComplete = percentage === 100;
  // Image URL helper
  const imgUrl = (path) => {
    return `/${path}`;
  };

  // Upload photo
  const uploadPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);

    const fd = new FormData();
    fd.append('profile_picture', file);

    try {
      const res = await api.post('/profile', fd, {
        headers:{ 
          "Content-Type": "multipart/form-data"
        }
      }); // PUT → works
      const updatedProfile = res.data.data;
      setProfile(updatedProfile);
      // updateUser({ profile: updatedProfile });
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setUploading(false);
    }
  };

  // Submit guarantor
  const submitGuarantor = async () => {
    if (!guarantor.id_card || !guarantor.full_name || !guarantor.phone) {
      alert('Please fill all fields and upload ID card');
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('full_name', guarantor.full_name);
    fd.append('phone', guarantor.phone);
    fd.append('relationship', guarantor.relationship);
    fd.append('address', guarantor.address);
    fd.append('id_card', guarantor.id_card);

    try {
      const res = await api.post('/agent/guarantor', fd, {
        headers:{ 
          "Content-Type": "multipart/form-data"
        }
      }); // CHANGE IF YOUR ROUTE IS DIFFERENT
      setProfile(p => ({ ...p, guarantors: [...(p.guarantors || []), res.data.data] }));
      setGuarantor({ full_name: '', phone: '', relationship: '', address: '', id_card: null });
      alert('Guarantor added!');
    } catch (err) {
      alert('Guarantor failed: ' + (err.response?.data?.message || 'Check route'));
    } finally {
      setUploading(false);
    }
  };

  // Add qualification
  const addQualification = async () => {
    if (!newQual.qualification_name || !newQual.institution || !newQual.year_obtained) {
      alert('Please fill all fields');
      return;
    }

    setLoadingQual(true);
    try {
      const res = await api.post('/profile/qualifications', newQual);
      setQualifications([...qualifications, res.data.data]);
      setNewQual({ qualification_name: '', institution: '', year_obtained: '' });
      alert('Qualification added!');
    } catch (err) {
      alert('Failed to add qualification: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoadingQual(false);
    }
  };

  // Add experience - NOW WITH LOADING
  const addExperience = async () => {
    if (!newExp.institution_name || !newExp.position) {
      alert('Please fill required fields');
      return;
    }

    setLoadingExp(true);
    try {
      const res = await api.post('/profile/work-experiences', newExp);
      setExperiences([...experiences, res.data.data]);
      setNewExp({
        institution_name: '', position: '', start_date: '', end_date: '', responsibilities: ''
      });
      alert('Experience added!');
    } catch (err) {
      alert('Failed to add experience: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoadingExp(false);
    }
  };

  const next = () => setCurrentStep(c => Math.min(c + 1, steps.length));
  const prev = () => setCurrentStep(c => Math.max(c - 1, 1));

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="text-center mb-10">
        <h1 className="text-3xl font-bold">Complete Your Profile</h1>
        <p className="text-muted mt-2">Get visible to customers faster</p>
      </div>

      {/* Progress Circle */}
      <div className="flex justify-center mb-12">
        <div className="relative">
          <svg className="w-40 h-40 -rotate-90">
            <circle cx="80" cy="80" r="70" stroke="#e5e7eb" strokeWidth="12" fill="none" />
            <circle cx="80" cy="80" r="70" stroke="url(#grad)" strokeWidth="12" fill="none"
              strokeDasharray={`${(439.8 * percentage) / 100} 439.8`}
              className="transition-all duration-1000" />
              <linearGradient id="grad">
              <stop offset="0%" stopColor="#0A66C2" />
              <stop offset="100%" stopColor="#057642" />
            </linearGradient>
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center justify-center">
            <span className="text-4xl font-bold text-primary">{percentage}%</span>
            <span className="text-sm text-muted">Complete</span>
          </div>
        </div>
      </div>

      {/* Steps */}
     <div className="w-full max-w-4xl mx-auto mb-16">
  <div className="flex items-center justify-between relative">
    {/* Background line */}
    <div className="absolute left-0 top-1/2 w-full h-1 bg-gray-200 -translate-y-1/2 -z-10" />
    
    {/* Progress line */}
    <div 
      className="absolute left-0 top-1/2 h-1 bg-primary transition-all duration-700 ease-out -translate-y-1/2 -z-10"
      style={{ width: `${((completed / steps.length) * 100).toFixed(0)}%` }}
    />

    {steps.map((step, i) => {
      const Icon = step.icon;
      const isCompleted = step.id < currentStep || 
        (step.id === currentStep && (
          step.id === 1 ? profile.profile_picture :
          step.id === 2 ? profile.guarantors?.length :
          step.id === 3 ? qualifications.length :
          experiences.length
        ));
      const isActive = step.id === currentStep;

      return (
        <div key={step.id} className="flex flex-col items-center relative z-10">
          <div className={`
            w-14 h-14 rounded-full flex items-center justify-center transition-all duration-500
            ${isCompleted ? 'bg-primary text-white shadow-lg scale-110' : ''}
            ${isActive ? 'bg-primary/20 text-primary ring-4 ring-primary/30 scale-110' : ''}
            ${!isCompleted && !isActive ? 'bg-white border-2 border-gray-300 text-gray-500' : ''}
          `}>
            {isCompleted ? (
              <CheckCircle2 size={32} className="drop-shadow" />
            ) : (
              <Icon size={26} />
            )}
          </div>
          <span className={`
            mt-3 text-sm font-medium transition-colors
            ${isActive ? 'text-primary font-semibold' : 'text-gray-600'}
          `}>
            {step.name}
          </span>
        </div>
      );
    })}
  </div>
</div>
      <div ref={contentRef} className="bg-white rounded-2xl shadow-xl p-8">

        {/* Step 1: Photo */}
        {currentStep === 1 && (
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-8">Upload a Clear Photo</h2>
            <div className="max-w-sm mx-auto">
              {profile.profile_picture ? (
                <div className="relative inline-block">
                  <img src={process.env.REACT_APP_IMAGE_URL+profile.profile_picture} alt="You"
                    className="w-64 h-64 object-cover rounded-full shadow-lg" />
                  <label className="absolute bottom-4 right-4 bg-primary text-white p-4 rounded-full shadow-lg cursor-pointer hover:scale-110 transition">
                    <Camera size={24} />
                    <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
                  </label>
                </div>
              ) : (
                <label className="cursor-pointer">
                  <div className="border-4 border-dashed border-gray-300 rounded-full w-64 h-64 mx-auto flex flex-col items-center justify-center hover:border-primary transition">
                    <Upload size={48} className="text-gray-400 mb-4" />
                    <span className="text-lg font-medium">Click to upload</span>
                    <span className="text-sm text-gray-500">Clear headshot, smiling</span>
                  </div>
                  <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
                </label>
              )}
            </div>
            <button onClick={next} disabled={uploading}
              className="mt-10 bg-primary text-white px-10 py-4 rounded-xl font-bold text-lg hover:bg-primary/90 disabled:opacity-60">
              {uploading ? 'Uploading...' : 'Continue →'}
            </button>
          </div>
        )}

        {/* Step 2: Guarantor */}
        {currentStep === 2 && (
          <div>
            <h2 className="text-2xl font-bold text-center mb-8">Add Guarantor</h2>
            {profile.guarantors?.length > 0 ? (
              <div className="text-center py-12">
                <CheckCircle2 size={80} className="mx-auto text-green-500 mb-4" />
                <p className="text-lg">Guarantor already added</p>
              </div>
            ) : (
              <div className="max-w-2xl mx-auto space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <input placeholder="Full Name" value={guarantor.full_name}
                    onChange={e => setGuarantor({ ...guarantor, full_name: e.target.value })}
                    className="px-5 py-4 border rounded-xl" />
                  <input placeholder="Phone Number" value={guarantor.phone}
                    onChange={e => setGuarantor({ ...guarantor, phone: e.target.value })}
                    className="px-5 py-4 border rounded-xl" />
                  <input placeholder="Relationship (e.g. Uncle)" value={guarantor.relationship}
                    onChange={e => setGuarantor({ ...guarantor, relationship: e.target.value })}
                    className="px-5 py-4 border rounded-xl" />
                  <input placeholder="Home Address" value={guarantor.address}
                    onChange={e => setGuarantor({ ...guarantor, address: e.target.value })}
                    className="px-5 py-4 border rounded-xl md:col-span-2" />
                </div>

                <label className="block">
                  <div className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer hover:border-primary">
                    <Upload size={40} className="mx-auto mb-3 text-gray-400" />
                    <span>{guarantor.id_card ? guarantor.id_card.name : 'Upload ID Card (PDF or Image)'}</span>
                    <input type="file" className="hidden"
                      onChange={e => setGuarantor({ ...guarantor, id_card: e.target.files[0] })} />
                  </div>
                </label>

                <button onClick={submitGuarantor} disabled={uploading}
                  className="w-full bg-primary text-white py-4 rounded-xl font-bold disabled:opacity-60">
                  {uploading ? 'Submitting...' : 'Submit Guarantor'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Qualifications */}
        {currentStep === 3 && (
          <div>
            <h2 className="text-2xl font-bold text-center mb-8">Qualifications</h2>
            <div className="max-w-2xl mx-auto space-y-4">
              {qualifications.map(q => (
                <div key={q.id} className="bg-gray-50 p-4 rounded-xl flex justify-between items-center">
                  <div>
                    <p className="font-semibold">{q.qualification_name}</p>
                    <p className="text-sm text-gray-600">{q.institution} • {q.year_obtained}</p>
                  </div>
                  <button onClick={() => {
                    if (window.confirm('Delete?')) api.delete(`/profile/qualifications/${q.id}`).then(() => {
                      setQualifications(prev => prev.filter(x => x.id !== q.id));
                    });
                  }} className="text-red-500"><Trash2 size={20} /></button>
                </div>
              ))}

              <div className="grid md:grid-cols-3 gap-3">
                <input placeholder="e.g. First Aid Certificate" value={newQual.qualification_name}
                  onChange={e => setNewQual({ ...newQual, qualification_name: e.target.value })}
                  className="px-4 py-3 border rounded-xl" />
                <input placeholder="Institution" value={newQual.institution}
                  onChange={e => setNewQual({ ...newQual, institution: e.target.value })}
                  className="px-4 py-3 border rounded-xl" />
                <input type="number" placeholder="Year" value={newQual.year_obtained}
                  onChange={e => setNewQual({ ...newQual, year_obtained: e.target.value })}
                  className="px-4 py-3 border rounded-xl" />
              </div>
              <button 
                onClick={addQualification}
                disabled={loadingQual}
                className="w-full bg-primary text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loadingQual ? (
                  <>Adding...</>
                ) : (
                  <>
                    <Plus size={20} /> Add Qualification
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Experience */}
        {currentStep === 4 && (
          <div>
            <h2 className="text-2xl font-bold text-center mb-8">Work Experience</h2>
            <div className="max-w-2xl mx-auto space-y-4">
              {experiences.map(exp => (
                <div key={exp.id} className="bg-gray-50 p-4 rounded-xl flex justify-between items-start">
                  <div>
                    <p className="font-semibold">{exp.position} — {exp.institution_name}</p>
                    <p className="text-sm text-gray-600">
                      {exp.start_date} – {exp.end_date || 'Present'}
                    </p>
                    {exp.responsibilities && <p className="text-sm mt-2 text-gray-700">{exp.responsibilities}</p>}
                  </div>
                  <button 
                onClick={addExperience}
                disabled={loadingExp}
                className="w-full bg-primary text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loadingExp ? (
                  <>Adding...</>
                ) : (
                  <>
                    <Plus size={20} /> Add Experience
                  </>
                )}
              </button>
                </div>
              ))}

              <div className="space-y-4">
                <input placeholder="Employer / Institution" value={newExp.institution_name}
                  onChange={e => setNewExp({ ...newExp, institution_name: e.target.value })}
                  className="w-full px-4 py-3 border rounded-xl" />
                <input placeholder="Position (e.g. Nanny)" value={newExp.position}
                  onChange={e => setNewExp({ ...newExp, position: e.target.value })}
                  className="w-full px-4 py-3 border rounded-xl" />
                <div className="grid grid-cols-2 gap-3">
                  <input type="date" value={newExp.start_date}
                    onChange={e => setNewExp({ ...newExp, start_date: e.target.value })}
                    className="px-4 py-3 border rounded-xl" />
                  <input type="date" value={newExp.end_date}
                    onChange={e => setNewExp({ ...newExp, end_date: e.target.value })}
                    placeholder="Present" className="px-4 py-3 border rounded-xl" />
                </div>
                <textarea placeholder="Key responsibilities (optional)" rows={3}
                  value={newExp.responsibilities}
                  onChange={e => setNewExp({ ...newExp, responsibilities: e.target.value })}
                  className="w-full px-4 py-3 border rounded-xl" />
                <button onClick={addExperience}
                  className="w-full bg-primary text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2">
                  <Plus size={20} /> Add Experience
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-12">
          <button onClick={prev} className={`px-6 py-3 ${currentStep === 1 ? 'invisible' : 'text-gray-500'}`}>
            ← Back
          </button>

          {currentStep < steps.length ? (
            <button onClick={next}
              className="bg-primary text-white px-8 py-3 rounded-xl font-bold flex items-center gap-2">
              Next <ChevronRight />
            </button>
          ) : (
           <div className="text-2xl font-bold text-primary text-center w-full">
              {isProfileComplete ? (
                <>Congratulations! Your profile is now complete</>
              ) : (
                <>Almost there! Please complete all steps.</>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}